import React, { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Head from "next/head";
import Image from "next/image";
import { motion, useScroll, useSpring } from "framer-motion";

import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ScoreboardRoundedIcon from "@mui/icons-material/ScoreboardRounded";
import SportsHockeyRoundedIcon from "@mui/icons-material/SportsHockeyRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TvRoundedIcon from "@mui/icons-material/TvRounded";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import ThemeModeContext from "../context/ThemeContext";
import LandingLayout from "../components/layout/LandingLayout";

const commonFeatures = [
  {
    icon: AccessTimeRoundedIcon,
    title: "Хоккейный хронометраж",
    description:
      "Периоды, перерывы, запуск, остановка и ручная корректировка времени, овертайм и серия буллитов.",
  },
  {
    icon: ScoreboardRoundedIcon,
    title: "Счёт и события",
    description:
      "Управление счётом, периодами и основными событиями хоккейного матча.",
  },
  {
    icon: SportsHockeyRoundedIcon,
    title: "Удаления и тайм-ауты",
    description:
      "Контроль штрафного времени обеих команд и предусмотренных матчем тайм-аутов.",
  },
  {
    icon: GroupsRoundedIcon,
    title: "Составы команд",
    description:
      "Команды, номера игроков, фамилии, фотографии и логотипы.",
  },
  {
    icon: TvRoundedIcon,
    title: "Табло для зрителей",
    description:
      "Вывод времени, счёта, периода и удалений на отдельный экран.",
  },
  {
    icon: TuneRoundedIcon,
    title: "Настройка оформления",
    description:
      "Фон, размеры и расположение элементов табло под конкретную площадку.",
  },
];

const audienceItems = [
  {
    title: "Ледовые арены",
    description:
      "Проведение матчей и вывод информации на экран спортивного объекта.",
  },
  {
    title: "Спортивные школы",
    description:
      "Детские, учебные, товарищеские и официальные матчи.",
  },
  {
    title: "Любительские лиги",
    description:
      "Понятный хронометраж без сложного специализированного оборудования.",
  },
  {
    title: "Организаторы турниров",
    description:
      "Подготовка матчей и единое оформление на разных площадках.",
  },
  {
    title: "Судьи-секретари",
    description:
      "Основные действия оператора собраны в одном рабочем интерфейсе.",
  },
  {
    title: "Поставщики оборудования",
    description:
      "Программная часть для комплексного оснащения спортивных объектов.",
  },
];

const screenshots = [
  {
    id: "control",
    src: "/screens/control_panel.webp",
    title: "Панель управления матчем",
    description:
      "Таймер, счёт, периоды, удаления и основные действия оператора.",
    label: "Управление матчем",
    objectPosition: "top center",
  },
  {
    id: "scoreboard",
    src: "/screens/scoreboard.webp",
    title: "Табло для зрителей",
    description:
      "Крупное отображение времени, счёта, периода и удалений.",
    label: "Второй экран",
    objectPosition: "center",
  },
  {
    id: "roster",
    src: "/screens/roster.webp",
    title: "Составы команд",
    description:
      "Игроки, номера, фотографии и логотипы команд.",
    label: "Команды",
    objectPosition: "top center",
  },
  {
    id: "history",
    src: "/screens/match_history.webp",
    title: "История матчей",
    description:
      "Сохранённые игры и быстрый доступ к ранее созданным матчам.",
    label: "Матчи",
    objectPosition: "top center",
  },
  {
    id: "tournament",
    src: "/screens/tournament.webp",
    title: "Проведение турниров",
    description:
      "Создание турниров, расписание матчей и подведение результатов.",
    label: "Турниры",
    objectPosition: "top center",
  },
];

const faqItems = [
  {
    question: "Что такое СпортПодсчет Хоккей?",
    answer:
      "СпортПодсчет Хоккей — программа для проведения хоккейных матчей и управления хронометражем. Она позволяет оператору управлять игровым временем, счётом, периодами, перерывами, удалениями, тайм-аутами, овертаймом и серией буллитов, а также выводить хоккейное табло на отдельный экран.",
  },
  {
    question: "Какие функции есть в программе хоккейного хронометража?",
    answer:
      "В СпортПодсчет Хоккей доступны таймер матча, управление счётом, периоды и перерывы, удаления игроков, тайм-ауты, овертайм, буллиты, составы команд, логотипы, фотографии игроков и настраиваемое табло для зрителей.",
  },
  {
    question: "Можно ли использовать СпортПодсчет Хоккей как программу для хоккейного табло?",
    answer:
      "Да. Программа формирует отдельное зрительское табло, на котором отображаются названия и логотипы команд, счёт, игровое время, текущий период и удаления. Табло можно вывести на второй монитор, телевизор, проектор или светодиодный экран, принимающий видеосигнал с компьютера.",
  },
  {
    question: "Можно ли проводить хоккейный матч без интернета?",
    answer:
      "Да. Локальная версия СпортПодсчет Хоккей устанавливается на компьютер оператора и работает без постоянного подключения к интернету. Таймер, счёт, удаления и табло продолжают работать непосредственно на площадке.",
  },
  {
    question: "Чем отличается локальная версия СпортПодсчет Хоккей от онлайн-версии?",
    answer:
      "Локальная версия устанавливается на компьютер площадки, хранит данные на этом компьютере и не зависит от интернета. Онлайн-версия открывается через браузер, хранит матчи и команды на сервере и позволяет работать с разных компьютеров и мобильных устройств.",
  },
  {
    question: "Можно ли полноценно проводить матч в онлайн-версии?",
    answer:
      "Да. Онлайн-версия СпортПодсчет Хоккей позволяет управлять таймером, счётом, периодами, удалениями, тайм-аутами, овертаймом, буллитами и табло во время реального матча. Для стабильной работы требуется надёжное интернет-соединение. При этом кратковременное отключение интернета не влияет на работоспособность системы",
  },
  {
    question: "Можно ли управлять хоккейным матчем с телефона?",
    answer:
      "Да. В онлайн-версии панель управления можно открыть в браузере на смартфоне, планшете или другом компьютере. Оператор может управлять временем, счётом, периодами и событиями матча, пока зрительское табло отображается на отдельном экране.",
  },
  {
    question: "Какое оборудование нужно для работы СпортПодсчет Хоккей?",
    answer:
      "Для работы потребуется компьютер оператора. Для показа хоккейного табло зрителям можно подключить второй монитор, телевизор, проектор или светодиодный экран. Для онлайн-версии дополнительно необходимо стабильное интернет-соединение.",
  },
  {
    question: "Можно ли подключить программу к телевизору, проектору или LED-экрану?",
    answer:
      "Да. Зрительское табло выводится как отдельное изображение с компьютера. Его можно передать через HDMI, DisplayPort или другое поддерживаемое видеоподключение на телевизор, проектор, монитор или светодиодный экран.",
  },
  {
    question: "Поддерживает ли программа удаления, тайм-ауты, овертайм и буллиты?",
    answer:
      "Да. СпортПодсчет Хоккей поддерживает основные сценарии хоккейного матча: периоды, перерывы, удаления игроков, штрафное время, командные тайм-ауты, овертайм и серию послематчевых буллитов.",
  },
  {
    question: "Можно ли настроить внешний вид хоккейного табло?",
    answer:
      "Да. Пользователь может менять фон, логотипы команд, размеры и расположение элементов, оформление счёта, таймера, периода и удалений. Табло можно адаптировать под разрешение и формат конкретного экрана.",
  },
  {
    question: "Можно ли сохранять команды и составы игроков?",
    answer:
      "Да. В программе можно создавать команды, добавлять игроков, номера, фамилии, фотографии и логотипы. Сохранённые команды и составы можно использовать при подготовке следующих матчей.",
  },
  {
    question: "Какая версия лучше подходит для официальных соревнований?",
    answer:
      "Выбор зависит от условий площадки. При нестабильном интернете предпочтительнее локальная версия, которая работает автономно. Онлайн-версия также может использоваться для проведения соревнований при наличии стабильного проводного интернета и резервного канала связи.",
  },
  {
    question: "Сколько стоит программа СпортПодсчет Хоккей?",
    answer:
      "Стоимость зависит от выбранной версии, количества рабочих мест, необходимости настройки табло, обучения оператора и технической поддержки. Для расчёта стоимости необходимо указать тип площадки, используемое оборудование и предполагаемый сценарий работы.",
  },
];



function ScrollProgressBar() {
  const { scrollYProgress } = useScroll();

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 26,
    restDelta: 0.001,
  });

  return (
    <Box
      component={motion.div}
      style={{
        scaleX,
        transformOrigin: "0% 50%",
      }}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: "primary.main",
        zIndex: 9999,
      }}
    />
  );
}

function Reveal({
  children,
  delay = 0,
  fullHeight = false,
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 24,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
        amount: 0.1,
      }}
      transition={{
        duration: 0.55,
        delay,
        ease: "easeOut",
      }}
      style={{
        width: "100%",
        height: fullHeight ? "100%" : "auto",
      }}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
  align = "left",
}) {
  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: align === "center" ? "auto" : 0,
        mb: {
          xs: 4,
          md: 6,
        },
        textAlign: align,
      }}
    >
      {eyebrow && (
        <Typography
          component="p"
          sx={{
            mb: 1.5,
            color: "primary.main",
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: "0.09em",
            textTransform: "uppercase",
          }}
        >
          {eyebrow}
        </Typography>
      )}

      <Typography
        component="h2"
        sx={{
          fontSize: {
            xs: "2rem",
            md: "3rem",
          },
          lineHeight: 1.12,
          fontWeight: 800,
          letterSpacing: "-0.035em",
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          color="text.secondary"
          sx={{
            mt: 2,
            fontSize: {
              xs: 16,
              md: 18,
            },
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

function VersionCard({
  type,
  title,
  subtitle,
  highlight,
  description,
  features,
  recommendation,
  icon: Icon,
  buttonText,
  onClick,
  isDark,
}) {
  const isLocal = type === "local";

  const accentColor = isLocal ? "#0073FF" : "#00A7D8";

  const accentBackground = isLocal
    ? isDark
      ? "rgba(0,115,255,0.15)"
      : "rgba(0,115,255,0.08)"
    : isDark
      ? "rgba(0,167,216,0.15)"
      : "rgba(0,167,216,0.08)";

  const borderColor = isLocal
    ? isDark
      ? "rgba(0,115,255,0.42)"
      : "rgba(0,115,255,0.24)"
    : isDark
      ? "rgba(0,167,216,0.42)"
      : "rgba(0,167,216,0.24)";

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: {
          xs: 3,
          sm: 4,
          md: 4.5,
        },
        borderRadius: 5,
        border: "1px solid",
        borderColor,
        bgcolor: "background.paper",
        boxShadow: isDark
          ? "0 22px 60px rgba(0,0,0,0.2)"
          : "0 22px 60px rgba(35,70,120,0.08)",
        transition:
          "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isDark
            ? "0 28px 70px rgba(0,0,0,0.3)"
            : "0 28px 70px rgba(35,70,120,0.13)",
        },
      }}
    >
      {/* Декоративный круг */}

      <Box
        sx={{
          position: "absolute",
          width: 250,
          height: 250,
          right: -110,
          top: -130,
          borderRadius: "50%",
          bgcolor: accentBackground,
          pointerEvents: "none",
        }}
      />

      {/* Иконка */}

      <Box
        sx={{
          position: "relative",
          width: 58,
          height: 58,
          display: "grid",
          placeItems: "center",
          borderRadius: 3,
          color: "#FFFFFF",
          bgcolor: accentColor,
          boxShadow: `0 12px 28px ${accentBackground}`,
        }}
      >
        <Icon
          sx={{
            fontSize: 32,
          }}
        />
      </Box>

      {/* Название */}

      <Typography
        component="h3"
        sx={{
          position: "relative",
          mt: 3,
          fontSize: {
            xs: "1.9rem",
            md: "2.35rem",
          },
          lineHeight: 1.12,
          fontWeight: 850,
          letterSpacing: "-0.04em",
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          position: "relative",
          mt: 0.8,
          color: accentColor,
          fontSize: 16,
          fontWeight: 800,
        }}
      >
        {subtitle}
      </Typography>

      {/* Главное отличие */}

      <Box
        sx={{
          position: "relative",
          mt: 2.5,
          px: 2,
          py: 1.5,
          borderRadius: 2.5,
          bgcolor: accentBackground,
          border: "1px solid",
          borderColor,
        }}
      >
        <Typography
          sx={{
            color: accentColor,
            fontSize: {
              xs: 16,
              md: 17,
            },
            fontWeight: 850,
          }}
        >
          {highlight}
        </Typography>
      </Box>

      {/* Описание */}

      <Typography
        color="text.secondary"
        sx={{
          position: "relative",
          mt: 2.5,
          fontSize: 16,
          lineHeight: 1.7,
        }}
      >
        {description}
      </Typography>

      {/* Отличительные особенности */}

      <Stack
        spacing={1.5}
        sx={{
          position: "relative",
          mt: 3,
        }}
      >
        {features.map((item) => (
          <Stack
            key={item}
            direction="row"
            spacing={1.1}
            alignItems="flex-start"
          >
            <CheckCircleRoundedIcon
              sx={{
                mt: 0.2,
                color: accentColor,
                fontSize: 20,
                flex: "0 0 auto",
              }}
            />

            <Typography
              sx={{
                lineHeight: 1.55,
                fontWeight: 650,
              }}
            >
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* Нижняя часть карточки */}

      <Box
        sx={{
          position: "relative",
          mt: "auto",
          pt: 4,
        }}
      >
        <Typography
          sx={{
            mb: 2.5,
            color: "text.secondary",
            lineHeight: 1.6,
          }}
        >
          <Box
            component="span"
            sx={{
              color: "text.primary",
              fontWeight: 800,
            }}
          >
            Рекомендуем:
          </Box>{" "}
          {recommendation}
        </Typography>

        <Button
          variant={isLocal ? "contained" : "outlined"}
          size="large"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={onClick}
          sx={{
            minHeight: 52,
            px: 3.25,
            borderRadius: 2.5,
            borderColor: accentColor,
            bgcolor: isLocal ? accentColor : "transparent",
            color: isLocal ? "#FFFFFF" : accentColor,
            textTransform: "none",
            fontWeight: 800,
            "&:hover": {
              borderColor: accentColor,
              bgcolor: isLocal
                ? accentColor
                : accentBackground,
            },
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Paper>
  );
}


export default function LandingHomePage() {
  const router = useRouter();
  const { mode } = useContext(ThemeModeContext);

  const [user, setUser] = useState(null);

  const isDark = mode === "dark";

  const softBackground = isDark
    ? "rgba(255,255,255,0.035)"
    : "#F6F8FC";

  const cardBorder = isDark
    ? "1px solid rgba(255,255,255,0.09)"
    : "1px solid rgba(17,24,39,0.08)";

  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");

      if (
        savedUser &&
        savedUser.trim().startsWith("{")
      ) {
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.warn(
        "Не удалось прочитать данные пользователя:",
        error
      );
    }
  }, []);

  const openDemo = () => {
    router.push("/demo");
  };

  const openLocal = () => {
    router.push("/contacts");
  };

  const openOnline = () => {
    router.push(user ? "/dashboard" : "/login");
  };

  const openContacts = () => {
    router.push("/contacts");
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const [openedScreenshot, setOpenedScreenshot] = useState(null);

  const closeScreenshot = () => {
    setOpenedScreenshot(null);
  };

  return (
    <>
      <Head>
        <title>
          СпортПодсчет Хоккей — программа хоккейного хронометража и табло
        </title>

        <meta
          name="description"
          content="СпортПодсчет Хоккей — локальная и онлайн-программа для проведения хоккейных матчей, управления временем, счётом, удалениями и вывода табло на отдельный экран."
        />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqStructuredData),
          }}
        />
      </Head>

      <ScrollProgressBar />

      <Box
        component="main"
        sx={{
          overflow: "hidden",
        }}
      >
        {/* Первый экран */}

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: {
              xs: 8,
              md: 11,
            },
            pb: {
              xs: 8,
              md: 11,
            },

            background: isDark
              ? `
    linear-gradient(
      90deg,
      rgba(5, 12, 24, 0.96) 0%,
      rgba(8, 19, 36, 0.88) 22%,
      rgba(8, 35, 65, 0.62) 100%
    ),
    url("/fon-hero.webp")
  `
              : `
    linear-gradient(
      90deg,
      rgba(248, 251, 255, 0.94) 0%,
      rgba(232, 243, 255, 0.82) 22%,
      rgba(221, 242, 248, 0.55) 100%
    ),
    url("/fon-hero.webp")
  `,

            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
            backgroundRepeat: "no-repeat, no-repeat",

            "&::before": {
              content: '""',
              position: "absolute",
              width: {
                xs: 360,
                md: 720,
              },
              height: {
                xs: 360,
                md: 720,
              },
              top: {
                xs: -170,
                md: -330,
              },
              right: {
                xs: -190,
                md: -230,
              },
              borderRadius: "50%",
              background: isDark
                ? "radial-gradient(circle, rgba(0,167,216,0.24) 0%, rgba(0,115,255,0.08) 45%, transparent 72%)"
                : "radial-gradient(circle, rgba(0,167,216,0.22) 0%, rgba(0,115,255,0.08) 45%, transparent 72%)",
              pointerEvents: "none",
            },

            "&::after": {
              content: '""',
              position: "absolute",
              width: {
                xs: 320,
                md: 600,
              },
              height: {
                xs: 320,
                md: 600,
              },
              left: {
                xs: -220,
                md: -300,
              },
              bottom: {
                xs: -210,
                md: -360,
              },
              borderRadius: "50%",
              background: isDark
                ? "radial-gradient(circle, rgba(0,115,255,0.17) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(0,115,255,0.13) 0%, transparent 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: 1400,
            }}
          >
            <Grid
              container
              spacing={{
                xs: 6,
                md: 7,
                lg: 9,
              }}
              alignItems="center"
            >
              {/* Текстовая часть */}

              <Grid size={{ xs: 12, md: 5 }}>
                <Reveal>
                  <Typography
                    component="p"
                    sx={{
                      mb: 2,
                      color: "primary.main",
                      fontSize: {
                        xs: 12,
                        md: 13,
                      },
                      lineHeight: 1.4,
                      fontWeight: 850,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    Хоккейный хронометраж и табло
                  </Typography>

                  <Typography
                    component="h1"
                    sx={{
                      maxWidth: 650,
                      fontSize: {
                        xs: "2.35rem",
                        sm: "2.9rem",
                        md: "3.35rem",
                        lg: "3.75rem",
                      },
                      lineHeight: 1.06,
                      fontWeight: 850,
                      letterSpacing: "-0.045em",
                    }}
                  >
                    СпортПодсчет Хоккей — всё для проведения хоккейного матча
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      maxWidth: 650,
                      mt: 3,
                      fontSize: {
                        xs: 16.5,
                        md: 18,
                      },
                      lineHeight: 1.7,
                    }}
                  >
                    Управляйте временем, счётом, периодами, удалениями и
                    составами команд. Выводите настраиваемое табло на
                    отдельный экран и проводите матч локально или через
                    браузер.
                  </Typography>

                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    spacing={2}
                    sx={{
                      mt: 4,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<ComputerRoundedIcon />}
                      onClick={openLocal}
                      sx={{
                        minHeight: 54,
                        px: 3.25,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontSize: 16,
                        fontWeight: 800,
                        boxShadow: "0 12px 30px rgba(0,115,255,0.22)",
                        "&:hover": {
                          boxShadow:
                            "0 16px 36px rgba(0,115,255,0.3)",
                        },
                      }}
                    >
                      Локальная версия
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<LanguageRoundedIcon />}
                      onClick={openOnline}
                      sx={{
                        minHeight: 54,
                        px: 3.25,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontSize: 16,
                        fontWeight: 800,
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.035)"
                          : "rgba(255,255,255,0.5)",
                        backdropFilter: "blur(12px)",
                        "&:hover": {
                          bgcolor: isDark
                            ? "rgba(0,115,255,0.1)"
                            : "rgba(255,255,255,0.85)",
                        },
                      }}
                    >
                      Онлайн-версия
                    </Button>
                  </Stack>
                </Reveal>
              </Grid>

              {/* Скриншот программы */}

              <Grid size={{ xs: 12, md: 7 }}>
                <Reveal delay={0.12}>
                  <Box
                    sx={{
                      position: "relative",
                      p: {
                        xs: 1.1,
                        sm: 1.5,
                      },

                    }}
                  >
                    <Image
                      src="/screens/SportPodschet.png"
                      alt="Панель управления хоккейным матчем СпортПодсчет Хоккей"
                      width={1600}
                      height={930}
                      priority
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />

                  </Box>
                </Reveal>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Выбор версии */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
            bgcolor: softBackground,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Reveal>
              <SectionHeading
                eyebrow="Два способа работы"
                title="Выберите подходящий формат"
                description="Функции проведения матча доступны в обеих версиях. Отличаются способ запуска, хранение данных и требования к интернет-соединению."
                align="center"
              />
            </Reveal>

            <Grid
              container
              spacing={{
                xs: 3,
                md: 3.5,
              }}
              alignItems="stretch"
            >
              {/* Local */}

              <Grid size={{ xs: 12, md: 6 }}>
                <Reveal>
                  <VersionCard
                    type="local"
                    title="СпортПодсчет Хоккей Local"
                    subtitle="Автономная работа на локальном компьютере"
                    highlight="Работает без интернета"
                    description="Программа устанавливается на компьютер оператора. Матч, табло и данные продолжают работать независимо от качества интернет-соединения."
                    features={[
                      "Данные хранятся на локальном компьютере",
                      "Не требуется постоянное подключение к интернету",
                      "Оптимально для работы на одном выделенном компьютере",
                    ]}
                    recommendation="выбирайте Local, когда важна максимальная автономность."
                    icon={ComputerRoundedIcon}
                    buttonText="Запросить локальную версию"
                    onClick={openLocal}
                    isDark={isDark}
                  />
                </Reveal>
              </Grid>

              {/* Online */}

              <Grid size={{ xs: 12, md: 6 }}>
                <Reveal delay={0.08}>
                  <VersionCard
                    type="online"
                    title="СпортПодсчет Хоккей Online"
                    subtitle="Проведение матчей через браузер"
                    highlight="Не требует установки"
                    description="Откройте систему в браузере, войдите в личный кабинет и проводите матч с доступом к данным с разных компьютеров и устройств."
                    features={[
                      "Матчи и команды хранятся на сервере",
                      "Доступ с разных компьютеров и устройств",
                      "Для работы требуется стабильное интернет-соединение",
                    ]}
                    recommendation="выбирайте Online, когда важны мобильность и удалённый доступ."
                    icon={CloudOutlinedIcon}
                    buttonText="Открыть Online"
                    onClick={openOnline}
                    isDark={isDark}
                  />
                </Reveal>
              </Grid>
            </Grid>

            {/* Общие функции */}

            <Reveal delay={0.1}>
              <Paper
                elevation={0}
                sx={{
                  mt: {
                    xs: 3,
                    md: 4,
                  },
                  px: {
                    xs: 2.5,
                    md: 4,
                  },
                  py: {
                    xs: 2.5,
                    md: 3,
                  },
                  borderRadius: 4,
                  border: cardBorder,
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.035)"
                    : "rgba(255,255,255,0.72)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <Grid
                  container
                  spacing={{
                    xs: 2,
                    md: 3,
                  }}
                  alignItems="center"
                >
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Stack
                      direction="row"
                      spacing={1.3}
                      alignItems="center"
                    >
                      <SportsHockeyRoundedIcon
                        sx={{
                          color: "primary.main",
                          fontSize: 30,
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: 17,
                          fontWeight: 850,
                        }}
                      >
                        В обеих версиях доступны
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, md: 9 }}>
                    <Stack
                      direction="row"
                      useFlexGap
                      flexWrap="wrap"
                      gap={1}
                    >
                      {[
                        "Таймер",
                        "Счёт",
                        "Периоды",
                        "Удаления",
                        "Тайм-ауты",
                        "Овертайм",
                        "Буллиты",
                        "Составы команд",
                        "Табло на отдельном экране",
                        "И многое другое...",
                      ].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            px: 1.5,
                            py: 0.8,
                            borderRadius: 2,
                            bgcolor: isDark
                              ? "rgba(0,115,255,0.12)"
                              : "rgba(0,115,255,0.07)",
                            color: "text.primary",
                            fontSize: 14,
                            lineHeight: 1.3,
                            fontWeight: 700,
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Reveal>
          </Container>
        </Box>


        {/* Возможности */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 12,
            },
            bgcolor: softBackground,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Reveal>
              <SectionHeading
                eyebrow="Возможности"
                title="Всё необходимое для хоккейного матча"
                description="Основные функции хронометража собраны в одном понятном интерфейсе."
                align="center"
              />
            </Reveal>

            <Grid container spacing={2.5}>
              {commonFeatures.map((item, index) => {
                const Icon = item.icon;

                return (
                  <Grid
                    key={item.title}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <Reveal delay={(index % 3) * 0.06}>
                      <Paper
                        elevation={0}
                        sx={{
                          height: "100%",
                          p: {
                            xs: 3,
                            md: 3.5,
                          },
                          borderRadius: 4,
                          border: cardBorder,
                          bgcolor: "background.paper",
                          transition:
                            "transform .25s ease, box-shadow .25s ease",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            boxShadow: isDark
                              ? "0 18px 45px rgba(0,0,0,.25)"
                              : "0 18px 45px rgba(42,71,120,.10)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 54,
                            height: 54,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: 2.8,
                            bgcolor: isDark
                              ? "rgba(0,115,255,0.17)"
                              : "rgba(0,115,255,0.09)",
                            color: "primary.main",
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: 29,
                            }}
                          />
                        </Box>

                        <Typography
                          component="h3"
                          sx={{
                            mt: 2.5,
                            fontSize: 20,
                            fontWeight: 800,
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography
                          color="text.secondary"
                          sx={{
                            mt: 1.2,
                            lineHeight: 1.7,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    </Reveal>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* Разделитель */}
        <Box
          aria-hidden="true"
          sx={{
            width: '100%',
            maxWidth: 960,
            mx: 'auto',
            my: { xs: 5, md: 7 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: '1px',
              background:
                'linear-gradient(90deg, transparent 0%, #D8E4F2 100%)',
            }}
          />

          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#AFC8E6',
              boxShadow: '0 0 0 5px rgba(175, 200, 230, 0.14)',
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              flex: 1,
              height: '1px',
              background:
                'linear-gradient(90deg, #D8E4F2 0%, transparent 100%)',
            }}
          />
        </Box>


        {/* Скриншоты */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
            bgcolor: softBackground,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Reveal>
              <SectionHeading
                eyebrow="Интерфейс"
                title="Посмотрите СпортПодсчет Хоккей в работе"
                description="Панель управления матчем, табло, составы команд и другие инструменты в одной системе."
                align="center"
              />
            </Reveal>

            {/* Bento-галерея */}

            <Box
              sx={{
                display: "grid",

                gap: {
                  xs: 2,
                  md: 2.5,
                },

                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },

                gridTemplateAreas: {
                  xs: `
            "control"
            "scoreboard"
            "roster"
            "history"
            "tournament"
          `,

                  sm: `
            "control control"
            "scoreboard roster"
            "history tournament"
          `,

                  md: `
            "control control scoreboard"
            "roster history tournament"
          `,
                },

                gridTemplateRows: {
                  xs: "repeat(5, 360px)",
                  sm: "440px 340px 340px",
                  md: "470px 310px",
                },
              }}
            >
              {screenshots.map((item, index) => {
                const isMain = item.id === "control";
                const isScoreboard = item.id === "scoreboard";

                return (
                  <Box
                    key={item.id}
                    sx={{
                      gridArea: item.id,

                      width: "100%",
                      height: "100%",

                      minWidth: 0,
                      minHeight: 0,
                    }}
                  >
                    <Reveal
                      delay={index * 0.05}
                      fullHeight
                    >
                      <Paper
                        elevation={0}
                        onClick={() => setOpenedScreenshot(item)}
                        role="button"
                        tabIndex={0}
                        aria-label={`Увеличить изображение: ${item.title}`}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setOpenedScreenshot(item);
                          }
                        }}
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                          overflow: "hidden",

                          cursor: "zoom-in",
                          outline: "none",

                          borderRadius: {
                            xs: 3,
                            md: 4,
                          },

                          border: cardBorder,

                          bgcolor: isDark
                            ? "#101827"
                            : "#E9EEF5",

                          boxShadow: isMain
                            ? isDark
                              ? "0 28px 70px rgba(0,0,0,0.32)"
                              : "0 28px 70px rgba(36,70,115,0.16)"
                            : isDark
                              ? "0 14px 38px rgba(0,0,0,0.18)"
                              : "0 14px 38px rgba(36,70,115,0.08)",

                          transition:
                            "transform .3s ease, box-shadow .3s ease",

                          "&:hover": {
                            transform: "translateY(-5px)",

                            boxShadow: isDark
                              ? "0 28px 65px rgba(0,0,0,0.34)"
                              : "0 28px 65px rgba(36,70,115,0.16)",

                            "& .screenshot-image": {
                              transform: "scale(1.025)",
                            },

                            "& .screenshot-overlay": {
                              opacity: 1,
                            },

                          },

                          "&:focus-visible": {
                            outline: "3px solid rgba(0,115,255,0.35)",
                            outlineOffset: 3,
                          },
                        }}
                      >
                        {/* Изображение */}

                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            overflow: "hidden",
                          }}
                        >
                          <Image
                            className="screenshot-image"
                            src={item.src}
                            alt={item.title}
                            fill
                            sizes={
                              isMain
                                ? "(max-width: 600px) 100vw, (max-width: 900px) 100vw, 66vw"
                                : "(max-width: 600px) 100vw, (max-width: 900px) 50vw, 33vw"
                            }
                            style={{
                              objectFit: "cover",
                              objectPosition:
                                item.objectPosition || "top center",
                              transition: "transform .45s ease",
                            }}
                          />
                        </Box>

                        {/* Затемнение изображения */}

                        <Box
                          className="screenshot-overlay"
                          sx={{
                            position: "absolute",
                            inset: 0,

                            opacity: 0.9,

                            transition: "opacity .3s ease",

                            background: isDark
                              ? `
                          linear-gradient(
                            180deg,
                            rgba(5,10,20,0.01) 20%,
                            rgba(5,10,20,0.10) 48%,
                            rgba(5,10,20,0.94) 100%
                          )
                        `
                              : `
                          linear-gradient(
                            180deg,
                            rgba(10,25,45,0.01) 20%,
                            rgba(10,25,45,0.08) 48%,
                            rgba(10,25,45,0.92) 100%
                          )
                        `,
                          }}
                        />

                        {/* Верхняя плашка */}

                        <Box
                          sx={{
                            position: "absolute",

                            top: {
                              xs: 16,
                              md: 20,
                            },

                            left: {
                              xs: 16,
                              md: 20,
                            },

                            zIndex: 2,
                          }}
                        >
                          <Chip
                            label={item.label}
                            size="small"
                            sx={{
                              height: 30,
                              px: 0.5,

                              color: "#FFFFFF",

                              bgcolor: "rgba(10,20,35,0.66)",

                              backdropFilter: "blur(10px)",
                              WebkitBackdropFilter: "blur(10px)",

                              border:
                                "1px solid rgba(255,255,255,0.18)",

                              fontWeight: 750,
                            }}
                          />
                        </Box>

                        {/* Нижний текст */}

                        <Box
                          sx={{
                            position: "absolute",

                            left: 0,
                            right: 0,
                            bottom: 0,

                            zIndex: 2,

                            p: {
                              xs: 2.5,
                              md: isMain ? 4 : 3,
                            },

                            color: "#FFFFFF",
                          }}
                        >
                          <Typography
                            component="h3"
                            sx={{
                              maxWidth: isMain ? 620 : 430,

                              fontSize: {
                                xs: "1.3rem",

                                md: isMain
                                  ? "2rem"
                                  : isScoreboard
                                    ? "1.55rem"
                                    : "1.35rem",
                              },

                              lineHeight: 1.15,

                              fontWeight: 850,

                              letterSpacing: "-0.025em",

                              textShadow:
                                "0 2px 16px rgba(0,0,0,0.45)",
                            }}
                          >
                            {item.title}
                          </Typography>

                          <Typography
                            sx={{
                              maxWidth: isMain ? 640 : 440,

                              mt: 1,

                              color: "rgba(255,255,255,0.82)",

                              fontSize: {
                                xs: 14,
                                md: isMain ? 16 : 14,
                              },

                              lineHeight: 1.55,

                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 2,

                              overflow: "hidden",

                              textShadow:
                                "0 2px 14px rgba(0,0,0,0.4)",
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </Paper>
                    </Reveal>
                  </Box>
                );
              })}
              <Dialog
                open={Boolean(openedScreenshot)}
                onClose={closeScreenshot}
                maxWidth={false}
                PaperProps={{
                  sx: {
                    position: "relative",

                    width: {
                      xs: "calc(100% - 24px)",
                      md: "calc(100% - 80px)",
                    },

                    height: {
                      xs: "calc(100% - 24px)",
                      md: "calc(100% - 80px)",
                    },

                    maxWidth: 1600,
                    maxHeight: 1000,

                    m: 0,

                    overflow: "hidden",

                    borderRadius: {
                      xs: 2,
                      md: 3,
                    },

                    bgcolor: "#070B12",
                    backgroundImage: "none",
                  },
                }}
                slotProps={{
                  backdrop: {
                    sx: {
                      bgcolor: "rgba(3, 8, 16, 0.88)",
                      backdropFilter: "blur(6px)",
                    },
                  },
                }}
              >
                {openedScreenshot && (
                  <>
                    <IconButton
                      onClick={closeScreenshot}
                      aria-label="Закрыть изображение"
                      sx={{
                        position: "absolute",
                        top: {
                          xs: 10,
                          md: 16,
                        },
                        right: {
                          xs: 10,
                          md: 16,
                        },

                        zIndex: 2,

                        color: "#FFFFFF",
                        bgcolor: "rgba(8, 14, 24, 0.7)",

                        border: "1px solid rgba(255,255,255,0.16)",

                        backdropFilter: "blur(10px)",

                        "&:hover": {
                          bgcolor: "rgba(8, 14, 24, 0.92)",
                        },
                      }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>

                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Image
                        src={openedScreenshot.src}
                        alt={openedScreenshot.title}
                        fill
                        priority
                        sizes="100vw"
                        style={{
                          objectFit: "contain",
                          objectPosition: "center",
                        }}
                      />
                    </Box>
                  </>
                )}
              </Dialog>
            </Box>


            {/* Кнопка демонстрации */}

            <Reveal delay={0.12}>
              <Box
                sx={{
                  mt: {
                    xs: 4,
                    md: 5,
                  },

                  textAlign: "center",
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{
                    maxWidth: 650,
                    mx: "auto",
                    mb: 2.5,
                    lineHeight: 1.7,
                  }}
                >
                  Посмотрите полный сценарий проведения хоккейного матча:
                  от создания команд до финальной сирены.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    <PlayCircleOutlineRoundedIcon />
                  }
                  onClick={openDemo}
                  sx={{
                    minHeight: 54,

                    px: 3.75,

                    borderRadius: 2.5,

                    textTransform: "none",

                    fontSize: 16,
                    fontWeight: 800,

                    boxShadow:
                      "0 12px 30px rgba(0,115,255,0.22)",

                    "&:hover": {
                      boxShadow:
                        "0 16px 36px rgba(0,115,255,0.3)",
                    },
                  }}
                >
                  Посмотреть демонстрацию
                </Button>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* Разделитель */}
        <Box
          aria-hidden="true"
          sx={{
            width: '100%',
            maxWidth: 960,
            mx: 'auto',
            my: { xs: 5, md: 7 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: '1px',
              background:
                'linear-gradient(90deg, transparent 0%, #D8E4F2 100%)',
            }}
          />

          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#AFC8E6',
              boxShadow: '0 0 0 5px rgba(175, 200, 230, 0.14)',
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              flex: 1,
              height: '1px',
              background:
                'linear-gradient(90deg, #D8E4F2 0%, transparent 100%)',
            }}
          />
        </Box>

        {/* Для кого */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 12,
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Reveal>
              <SectionHeading
                eyebrow="Для кого"
                title="Подходит площадкам разного уровня"
                description="От школьного матча до комплексного оснащения ледовой арены."
                align="center"
              />
            </Reveal>

            <Grid container spacing={2.2}>
              {audienceItems.map((item, index) => (
                <Grid
                  key={item.title}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                  }}
                >
                  <Reveal delay={(index % 3) * 0.05}>
                    <Paper
                      elevation={0}
                      sx={{
                        height: "100%",
                        p: 3,
                        borderRadius: 3.5,
                        border: cardBorder,
                        bgcolor: softBackground,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 19,
                          fontWeight: 800,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          lineHeight: 1.65,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>


        {/* Разделитель */}
        <Box
          aria-hidden="true"
          sx={{
            width: '100%',
            maxWidth: 960,
            mx: 'auto',
            my: { xs: 5, md: 7 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: '1px',
              background:
                'linear-gradient(90deg, transparent 0%, #D8E4F2 100%)',
            }}
          />

          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#AFC8E6',
              boxShadow: '0 0 0 5px rgba(175, 200, 230, 0.14)',
              flexShrink: 0,
            }}
          />

          <Box
            sx={{
              flex: 1,
              height: '1px',
              background:
                'linear-gradient(90deg, #D8E4F2 0%, transparent 100%)',
            }}
          />
        </Box>

        {/* Удалённое управление */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 12,
            },
            bgcolor: softBackground,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1200,
            }}
          >
            <Grid
              container
              spacing={{
                xs: 5,
                md: 8,
              }}
              alignItems="center"
            >
              {/* Изображение */}

              <Grid size={{ xs: 12, md: 5 }}>
                <Reveal>
                  <Box
                    sx={{
                      position: "relative",
                      p: {
                        xs: 1.2,
                        md: 1.5,
                      },
                      borderRadius: 4,
                      border: cardBorder,
                      bgcolor: "background.paper",
                      boxShadow: isDark
                        ? "0 24px 60px rgba(0,0,0,0.24)"
                        : "0 24px 60px rgba(35,70,120,0.1)",
                    }}
                  >
                    <Image
                      src="/phoneScrenshot.webp"
                      alt="Управление хоккейным матчем с телефона в СпортПодсчет Хоккей"
                      width={800}
                      height={900}
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 500,
                        objectFit: "contain",
                        display: "block",
                        borderRadius: 12,
                      }}
                    />
                  </Box>
                </Reveal>
              </Grid>

              {/* Текст */}

              <Grid size={{ xs: 12, md: 7 }}>
                <Reveal delay={0.08}>
                  <SectionHeading
                    eyebrow="Удалённое управление"
                    title="Управляйте матчем с телефона"
                    description="В онлайн-версии СпортПодсчет Хоккей оператор может открыть панель управления на смартфоне, планшете или другом компьютере. Табло при этом продолжает отображаться на отдельном экране площадки."
                  />

                  <Stack spacing={2.5}>
                    {/* Устройства */}

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          flex: "0 0 auto",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2.3,
                          color: "#00A7D8",
                          bgcolor: isDark
                            ? "rgba(0,167,216,0.15)"
                            : "rgba(0,167,216,0.08)",
                        }}
                      >
                        <DevicesRoundedIcon />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: 850,
                          }}
                        >
                          Телефон, планшет или компьютер
                        </Typography>

                        <Typography
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            lineHeight: 1.65,
                          }}
                        >
                          Откройте управление в браузере и работайте с
                          удобного устройства без установки отдельной
                          программы.
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Функции управления */}

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          flex: "0 0 auto",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2.3,
                          color: "primary.main",
                          bgcolor: isDark
                            ? "rgba(0,115,255,0.15)"
                            : "rgba(0,115,255,0.08)",
                        }}
                      >
                        <SportsHockeyRoundedIcon />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: 850,
                          }}
                        >
                          Основные действия оператора
                        </Typography>

                        <Typography
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            lineHeight: 1.65,
                          }}
                        >
                          Запускайте и останавливайте время, изменяйте
                          счёт и период, добавляйте удаления, тайм-ауты и
                          другие события матча.
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Табло */}

                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          flex: "0 0 auto",
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2.3,
                          color: "#00A7D8",
                          bgcolor: isDark
                            ? "rgba(0,167,216,0.15)"
                            : "rgba(0,167,216,0.08)",
                        }}
                      >
                        <TvRoundedIcon />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: 850,
                          }}
                        >
                          Табло на отдельном экране
                        </Typography>

                        <Typography
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            lineHeight: 1.65,
                          }}
                        >
                          Зрительское табло можно вывести на монитор,
                          телевизор, проектор или светодиодный экран, а
                          управление оставить на телефоне оператора.
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>

                  {/* Примечание */}

                  <Box
                    sx={{
                      mt: 3.5,
                      px: 2.5,
                      py: 2,
                      borderRadius: 3,
                      bgcolor: isDark
                        ? "rgba(0,167,216,0.1)"
                        : "rgba(0,167,216,0.06)",
                      border: isDark
                        ? "1px solid rgba(0,167,216,0.22)"
                        : "1px solid rgba(0,167,216,0.15)",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.2}
                      alignItems="flex-start"
                    >
                      <CloudOutlinedIcon
                        sx={{
                          mt: 0.1,
                          color: "#00A7D8",
                          flex: "0 0 auto",
                        }}
                      />

                      <Typography
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                        }}
                      >
                        Для удалённого управления и синхронного обновления
                        данных требуется стабильное интернет-соединение.
                      </Typography>
                    </Stack>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<LanguageRoundedIcon />}
                    onClick={openOnline}
                    sx={{
                      mt: 3.5,
                      minHeight: 52,
                      px: 3.5,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontSize: 16,
                      fontWeight: 800,
                    }}
                  >
                    Открыть онлайн-версию
                  </Button>
                </Reveal>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Пилот */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 12,
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1400,
            }}
          >
            <Reveal>
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  px: {
                    xs: 3,
                    sm: 5,
                    md: 8,
                  },
                  py: {
                    xs: 5,
                    md: 8,
                  },
                  borderRadius: {
                    xs: 4,
                    md: 6,
                  },
                  color: "#FFFFFF",
                  background:
                    "linear-gradient(135deg, #005BDA 0%, #0073FF 52%, #00A7D8 100%)",
                  boxShadow:
                    "0 30px 80px rgba(0,115,255,0.24)",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: 360,
                    height: 360,
                    right: -110,
                    top: -150,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.10)",
                  }}
                />

                <Grid
                  container
                  spacing={5}
                  alignItems="center"
                  sx={{
                    position: "relative",
                  }}
                >
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Typography
                      sx={{
                        mb: 1.5,
                        fontSize: 14,
                        fontWeight: 850,
                        letterSpacing: "0.09em",
                        textTransform: "uppercase",
                        opacity: 0.82,
                      }}
                    >
                      Пилотное тестирование СпортПодсчет Хоккей
                    </Typography>

                    <Typography
                      component="h2"
                      sx={{
                        maxWidth: 850,
                        fontSize: {
                          xs: "2.1rem",
                          md: "3.4rem",
                        },
                        lineHeight: 1.1,
                        fontWeight: 850,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Проведите первый матч с СпортПодсчет Хоккей
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 820,
                        mt: 2.2,
                        fontSize: {
                          xs: 16,
                          md: 19,
                        },
                        lineHeight: 1.7,
                        color: "rgba(255,255,255,0.86)",
                      }}
                    >
                      Поможем выбрать Local или Online, настроить
                      рабочее место и табло, обучить оператора и
                      провести первые учебные или реальные матчи.
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Stack
                      spacing={1.5}
                      alignItems={{
                        xs: "stretch",
                        md: "flex-end",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardRoundedIcon />}
                        onClick={openContacts}
                        sx={{
                          minHeight: 54,
                          px: 3.5,
                          borderRadius: 2.5,
                          bgcolor: "#FFFFFF",
                          color: "#005BDA",
                          textTransform: "none",
                          fontSize: 16,
                          fontWeight: 800,
                          "&:hover": {
                            bgcolor:
                              "rgba(255,255,255,0.90)",
                          },
                        }}
                      >
                        Подать заявку на пилот
                      </Button>

                      <Typography
                        sx={{
                          fontSize: 13,
                          opacity: 0.72,
                        }}
                      >
                        Условия обсуждаются индивидуально
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* FAQ */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 12,
            },
            bgcolor: softBackground,
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1100,
            }}
          >
            <Reveal>
              <SectionHeading
                eyebrow="Ответы"
                title="Частые вопросы"
                description="Главное о системе хронометража и управления матчами СпортПодсчет Хоккей."
                align="center"
              />
            </Reveal>

            <Stack spacing={1.5}>
              {faqItems.map((item, index) => (
                <Reveal
                  key={item.question}
                  delay={Math.min(index * 0.03, 0.15)}
                >
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      border: cardBorder,
                      borderRadius: "16px !important",
                      bgcolor: "background.paper",
                      overflow: "hidden",
                      "&:before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreRoundedIcon />}
                      sx={{
                        px: {
                          xs: 2.3,
                          md: 3,
                        },
                        py: 0.6,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 800,
                        }}
                      >
                        {item.question}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        px: {
                          xs: 2.3,
                          md: 3,
                        },
                        pb: 2.7,
                      }}
                    >
                      <Typography
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                        }}
                      >
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Reveal>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Финальный призыв */}

        <Box
          sx={{
            py: {
              xs: 8,
              md: 11,
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1000,
              textAlign: "center",
            }}
          >
            <Reveal>
              <Typography
                component="h2"
                sx={{
                  fontSize: {
                    xs: "2.15rem",
                    md: "3.4rem",
                  },
                  lineHeight: 1.1,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                Выберите свой формат СпортПодсчет Хоккей
              </Typography>

              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: 760,
                  mx: "auto",
                  mt: 2,
                  fontSize: {
                    xs: 16,
                    md: 18,
                  },
                  lineHeight: 1.7,
                }}
              >
                Обе версии предназначены для проведения матчей.
                Выберите автономный Local или доступный через
                браузер Online в зависимости от условий вашей
                площадки.
              </Typography>

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                justifyContent="center"
                spacing={2}
                sx={{
                  mt: 4,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<ComputerRoundedIcon />}
                  onClick={openLocal}
                  sx={{
                    minHeight: 54,
                    px: 3.5,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  Попробовать Local
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LanguageRoundedIcon />}
                  onClick={openOnline}
                  sx={{
                    minHeight: 54,
                    px: 3.5,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  Открыть Online
                </Button>

                <Button
                  variant="text"
                  size="large"
                  onClick={openContacts}
                  sx={{
                    minHeight: 54,
                    px: 2.5,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 800,
                  }}
                >
                  Задать вопрос
                </Button>
              </Stack>
            </Reveal>
          </Container>
        </Box>
      </Box>
    </>
  );
}

LandingHomePage.getLayout = (page) => (
  <LandingLayout>{page}</LandingLayout>
);